import React, { ReactNode, useState } from "react";
import Header from "@cloudscape-design/components/header";
import { TyreTable } from "./data list tyre/data";
import Table, { TableProps } from "@cloudscape-design/components/table";
import { useCollection } from "@cloudscape-design/collection-hooks";
import {
  CollectionPreferences,
  CollectionPreferencesProps,
} from "@cloudscape-design/components";
import Pagination from "@cloudscape-design/components/pagination";
import Box from "@cloudscape-design/components/box";
import Button from "@cloudscape-design/components/button";
import TextFilter from "@cloudscape-design/components/text-filter";
import { Link } from "react-router-dom";
import Icon from "@cloudscape-design/components/icon";
import StatusIndicator from "@cloudscape-design/components/status-indicator";

interface CellProps {
  Status: boolean;
}

const getFilterCounterText = (count: number = 0) =>
  `${count} ${count === 1 ? "match" : "matches"}`;

export interface VariationTableProps {
  TyreMaster: TyreTable[];
}

const TyreeStatus: React.FC<CellProps> = ({ Status }) => {
  let statusType: "success" | "error" = "error"; // status types (status indicator cloudscape)

  // Conditional rendering logic
  if (Status === true) {
    statusType = "success";
  } else if (Status === false) {
    statusType = "error";
  }

  return (
    <StatusIndicator type={statusType}>
      {statusType === "success"
        ? "Plug"
        : statusType === "error"
        ? "Unplug"
        : "Unknown Status"}
    </StatusIndicator>
  );
};

const columnDefinitions: TableProps<TyreTable>["columnDefinitions"] = [
  {
    header: "ID",
    cell: ({ id }) => id,
    sortingField: "id",
    width: "10%",
  },

  {
    header: "S/N",
    cell: ({ sn }) => <Link to={`/vehicles/tyres/${sn}`}>{sn}</Link>,
    sortingField: "sn",
    width: 100,
  },
  {
    header: "Sensor ID",
    cell: ({ sensorId }) => sensorId,
    sortingField: "sensorId",
    width: 100,
  },

  {
    header: "Brand",
    cell: ({ brand }) => brand,
    sortingField: "brand",
    width: 100,
  },
  {
    header: "Tyre Type",
    cell: ({ typetyre }) => typetyre,
    sortingField: "typetyre",
    width: 100,
  },
  {
    header: "Vehicle Code",
    cell: ({ VehicleCode }) => VehicleCode,

    /*(
      <Link to={`/Vehicle-Detail/${VehicleCode}`}>
        {VehicleCode}
      </Link>
    )*/
    sortingField: "VehicleCode",
    width: 100,
  },
  {
    header: "Position",
    cell: ({ tyreId }) => tyreId,
    sortingField: "tyreId",
    width: "10%",
  },

  {
    header: "Pressure",
    cell: ({ pressure }) => pressure,
    sortingField: "Pressure",
    width: 100,
  },
  {
    header: "Temperature",
    cell: ({ temperature }) => temperature,
    sortingField: "temperature",
    width: 100,
  },
  {
    header: "Battery ",
    cell: ({ battery }) => battery,
    sortingField: "battery",
    width: 200,
  },
  {
    header: "Status ",
    cell: TyreeStatus,
    sortingField: "Status",
    width: 100,
  },
];

const EmptyState = ({
  title,
  subtitle,
  action,
}: {
  title: string;
  subtitle: string;
  action: ReactNode;
}) => {
  return (
    <Box textAlign='center' color='inherit'>
      <Box variant='strong' textAlign='center' color='inherit'>
        {title}
      </Box>
      <Box variant='p' padding={{ bottom: "s" }} color='inherit'>
        {subtitle}
      </Box>
      {action}
    </Box>
  );
};

const getHeaderCounterText = (
  items: readonly TyreTable[] = [],
  selectedItems: readonly TyreTable[] = [],
  displayedItemsCount: number = 0
) => {
  return selectedItems && selectedItems.length > 0
    ? `(${selectedItems.length}/${displayedItemsCount}/${items.length})`
    : `(${displayedItemsCount}/${items.length})`;
};

export default function TyreList({ TyreMaster }: VariationTableProps) {
  const [preferences, setPreferences] = useState<
    CollectionPreferencesProps["preferences"]
  >({ pageSize: 10 });
  const {
    items,
    filterProps,
    actions,
    filteredItemsCount,
    paginationProps,
    collectionProps,
  } = useCollection<TyreTable>(TyreMaster, {
    filtering: {
      filteringFunction: (item, filteringText) => {
        const filterTextLower = filteringText.toLowerCase();
        const statusText = item.Status ? "plug" : "unplug";
        if (statusText === filterTextLower) {
          return true;
        }

        return (
          item.tyreId.toString().includes(filterTextLower) ||
          item.sn.toLowerCase().includes(filterTextLower) ||
          item.sensorId.toString().includes(filterTextLower) ||
          item.brand.toLowerCase().includes(filterTextLower) ||
          item.typetyre.toLowerCase().includes(filterTextLower) ||
          item.VehicleCode.toLowerCase().includes(filterTextLower) ||
          item.pressure.toString().includes(filterTextLower) ||
          item.temperature.toString().includes(filterTextLower) ||
          item.battery.toString().includes(filterTextLower) ||
          item.id.toString().includes(filterTextLower)
        );
      },

      noMatch: (
        <EmptyState
          title='No matches'
          subtitle='We can’t find a match.'
          action={
            <Button onClick={() => actions.setFiltering("")}>
              Clear filter
            </Button>
          }
        />
      ),
    },

    pagination: { pageSize: preferences?.pageSize },
    sorting: { defaultState: { sortingColumn: columnDefinitions[0] } },
    selection: {},
  });
  return (
    <>
      <Table<TyreTable>
        {...collectionProps}
        items={items}
        columnDefinitions={columnDefinitions}
        stickyHeader={true}
        resizableColumns={true}
        variant='container'
        ariaLabels={{
          selectionGroupLabel: "Items selection",
          itemSelectionLabel: ({ selectedItems }, item) => {
            const isItemSelected = selectedItems.filter(
              (i) => i.id === item.id
            ).length;
            return `${item.id} is ${isItemSelected ? "" : "not "}selected`;
          },
          tableLabel: "Master Tyre table",
        }}
        header={
          <Header
            variant='h1'
            counter={getHeaderCounterText(
              TyreMaster,
              collectionProps.selectedItems,
              items.length
            )}
          >
            Tyres
          </Header>
        }
        pagination={<Pagination {...paginationProps} />}
        filter={
          <TextFilter
            {...filterProps}
            filteringPlaceholder='Find Tyre'
            countText={getFilterCounterText(filteredItemsCount)}
          />
        }
        preferences={
          <CollectionPreferences
            preferences={preferences}
            pageSizePreference={{
              title: "select page size",
              options: [
                { value: 10, label: "10 resources" },
                { value: 20, label: "20 resources" },
                { value: 50, label: "50 resources" },
                { value: 100, label: "100 resources" },
              ],
            }}
            onConfirm={({ detail }) => setPreferences(detail)}
            title='preference'
            confirmLabel='Confrim'
            cancelLabel='Cancel'
          />
        }
        // selectionType="multi"
      />
    </>
  );
}
